import { Component, type ErrorInfo, type ReactNode } from 'react';
import { StatePanel } from '../ui';
import layout from '../ui/layout.module.css';
import { text } from '../strings';

/**
 * The one failure state a screen cannot render for itself: its own render throwing.
 *
 * Every other state on every other page is a value — `loading`, `empty`, `problem` — because the
 * transport returns its errors rather than throwing them. This catches what is left: a bug in a model,
 * a field that turned out not to be optional, a parser that a fixture never covered. React's behaviour
 * without a boundary is to unmount the whole tree, and with `<App>` at the root that is a **white
 * page** — no header, no nav, no address bar hint, nothing to press. That is a worse outcome than any
 * classified failure, and it is the one 4a would otherwise leave untouched.
 *
 * It wraps `children` **inside** the shell rather than around it, deliberately: the header, the nav and
 * the theme toggle are not what broke, and leaving them up means the reader can go somewhere else in
 * one click instead of reaching for the address bar.
 *
 * A class, because there is no hook form — `componentDidCatch` and `getDerivedStateFromError` are the
 * only API React offers, and this is the single class component in the app.
 */
interface BoundaryProps {
  readonly children: ReactNode;
  /**
   * Changes whenever the route does, which is what clears the error. Without it a reader who navigates
   * away from a broken page keeps the broken page: a boundary that has caught stays caught until its
   * state is reset, and React gives it no signal that anything changed underneath.
   */
  readonly resetKey: string;
}

interface BoundaryState {
  readonly caught: string | null;
}

export class ScreenBoundary extends Component<BoundaryProps, BoundaryState> {
  override state: BoundaryState = { caught: null };

  static getDerivedStateFromError(error: unknown): BoundaryState {
    /*
     * The message, and not the stack. A stack in the page is unreadable to the person looking at it,
     * and it is the same R9 hazard `shaka.util.Error` is: whatever threw may have a URL in it, and a
     * signed media URL carries `sig`, `n` and `ip`. The console keeps the full error either way —
     * `componentDidCatch` below puts it there — so nothing is lost for whoever is actually debugging.
     */
    return { caught: error instanceof Error ? error.message : String(error) };
  }

  override componentDidCatch(error: unknown, info: ErrorInfo): void {
    // The full object with its component stack, once, where a developer will look for it.
    console.error('[visious] a screen failed to render', error, info.componentStack);
  }

  override componentDidUpdate(previous: BoundaryProps): void {
    if (previous.resetKey !== this.props.resetKey && this.state.caught !== null) {
      this.setState({ caught: null });
    }
  }

  override render(): ReactNode {
    if (this.state.caught === null) return this.props.children;

    // A class component, so `useCopy` is unavailable — and unnecessary: a boundary that has caught is
    // rendered once and replaced by its retry, so there is no subscription for a language change to
    // move. `text()` reads the same active catalogue the hook would hand back.
    const words = text().shell.boundary;

    return (
      <div className={layout.page}>
        <StatePanel
          headingLevel="h1"
          title={words.title}
          detail={
            <>
              {words.detailBefore} {this.state.caught}
            </>
          }
          onRetry={() => this.setState({ caught: null })}
          retryLabel={words.retry}
        />
      </div>
    );
  }
}
